import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

import { DataService } from 'api/DataService'
import { parseCookies, setCookie } from 'nookies'

import {
  AddressFormSchema,
  initialAddressFormSchemaValue
} from 'components/ActivateAccountForms/Address'
import {
  BankFormSchema,
  initialBankDataFormValue
} from 'components/ActivateAccountForms/BankData'
import {
  InformationFormSchema,
  initialInformationFormSchemaValue
} from 'components/ActivateAccountForms/Information'

import { normalizeDocument } from 'utils/document'

import { UserStatus, useAuthContext } from './AuthContext'

type FormTypes = InformationFormSchema | AddressFormSchema | BankFormSchema
// SC-10710: era uma segunda lista de status, paralela ao `UserStatus` do
// AuthContext e ja fora de sincronia com ele (tinha `disabled`, que o outro nao
// tem). O cookie `nextauth.status` e escrito pelos dois, entao o tipo tem que
// ser um so.
type Status = UserStatus

export type OnboardingContextProps = {
  step: number
  fetching: boolean
  setStep: React.Dispatch<React.SetStateAction<number>>
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
  loading: boolean
  setGotLogoFromWebsite: React.Dispatch<React.SetStateAction<boolean>>
  gotLogoFromWebsite: boolean
  personalizeStep: number
  setPersonalizeStep: React.Dispatch<React.SetStateAction<number>>
  handleChange: (step: number, value: FormTypes) => void
  status: Status
  // SC-10941: cartão na Stone — o wizard coleta os campos que a Stone exige e troca o
  // passo de documentos (KYC da Zoop) pela prova de vida da Stone.
  isStone: boolean
  fetchOnboarding: () => void
  values: {
    1: InformationFormSchema
    2: AddressFormSchema
    3: BankFormSchema
  }
}

const initialOnboardingValues = {
  values: {
    1: initialInformationFormSchemaValue,
    2: initialAddressFormSchemaValue,
    3: initialBankDataFormValue
  }
} as OnboardingContextProps

// SC-10710: o wizard tem 6 posicoes (5 formularios + a tela de sucesso). Quem
// ja pediu a ativacao ou ja finalizou o onboarding cai na tela de sucesso, nao
// de volta em Documentos: `activation_requested`/`finished` apontavam para 5,
// que e o formulario de Documentos com o botao "Finalizar cadastro" ativo. Um
// segundo clique ali nao tem como dar certo, porque o merchant sai de `trial`
// na primeira ativacao e a API recusa.
export const SUCCESS_STEP = 6

const stepsMapper = {
  infos: 1,
  informations: 1,
  address: 2,
  personalized: 2,
  bank_account: 3,
  document_selfie: 4,
  document_identity: 4,
  document_document_with_photo: 4,
  document_residency_proof: 4,
  issued_billing: 4,
  activation_requested: SUCCESS_STEP,
  finished: SUCCESS_STEP
}

// Status em que a ativacao ja foi pedida (ou nem cabe mais): o merchant nao e
// mais `trial`, entao nenhum formulario do wizard aceita submit. Serve de rede
// para o caso do `steps` do backend estar atras do status do merchant.
export const ACTIVATION_DONE_STATUSES: UserStatus[] = [
  'checking',
  'enabled',
  'blocked',
  'suspended',
  'rejected'
]

const maxAge = 1000 * 60 * 60 * 24 * 6 // 6 dias

const OnboardingFormContext = createContext<OnboardingContextProps>(
  initialOnboardingValues
)

type OnboardingProviderProps = {
  children: React.ReactNode
}

export const OnboardingProvider = ({ children }: OnboardingProviderProps) => {
  const { user } = useAuthContext()
  const [step, setStep] = useState(1)
  const [fetching, setFetching] = useState(true)
  const [loading, setLoading] = useState(false)
  const [personalizeStep, setPersonalizeStep] = useState<number>(
    user?.logo ? 2 : 1
  )
  const [values, setValues] = useState(initialOnboardingValues.values)
  const [status, setStatus] = useState('' as Status)
  const [isStone, setIsStone] = useState(false)
  const [gotLogoFromWebsite, setGotLogoFromWebsite] = useState(false)

  const hasWindow = typeof window !== 'undefined'

  const handleChange = useCallback(
    (step: number, formValue: FormTypes) => {
      setValues((s) => ({ ...s, [step]: formValue }))
    },
    [setValues]
  )

  const fetchOnboarding = useCallback(async () => {
    setFetching(true)
    const url = '/v1/onboarding'
    const response = await DataService({ url, type: 'GET' })
    const data = response?.data?.record
    const isCPF = data?.informations?.document
      ? normalizeDocument(data.informations.document).length <= 11
      : true
    const stone = data?.kyc_flow === 'proof_of_life'
    setIsStone(stone)

    setValues((s) => ({
      ...s,
      1:
        { ...data?.informations, isCPF, isStone: stone } ??
        initialOnboardingValues.values[1],
      2: data?.address ?? initialOnboardingValues.values[2],
      3: data?.bank_account ?? initialOnboardingValues.values[3]
    }))
    const merchantStatus = response.data.merchant_status as Status

    setStatus(merchantStatus)
    setCookie(undefined, 'nextauth.status', merchantStatus, {
      maxAge
    })

    // SC-10710: o `merchant_status` manda. O `steps` do backend so registra o
    // que o merchant preencheu, e ele fica atras do status quando a ativacao ja
    // aconteceu (a aprovacao do KYC muda o merchant, nao os steps). Sem isso o
    // merchant ja ativo era devolvido ao formulario de Documentos.
    setStep(
      ACTIVATION_DONE_STATUSES.includes(merchantStatus)
        ? SUCCESS_STEP
        : stepsMapper[
            data?.step === 'informations' && user?.logo
              ? 'personalized'
              : data?.step || 'activation_requested'
          ]
    )
    setFetching(false)
  }, [setValues])

  useEffect(() => {
    const { ['nextauth.status']: local_status, ['nextauth.token']: token } =
      parseCookies()

    if (!token) return

    // SC-11480: conta ativa não busca o onboarding, mas o `fetching` nasce
    // `true` e só o fetch o desligava. Quem abria /ativar-conta já ativo ficava
    // no loader para sempre. Sem nada a preencher, vai direto à tela final.
    if (local_status === 'enabled') {
      setStatus('enabled')
      setStep(SUCCESS_STEP)
      setFetching(false)
      return
    }

    fetchOnboarding()
  }, [hasWindow && window?.location?.pathname])

  const value = useMemo(
    () => ({
      values,
      status,
      isStone,
      handleChange,
      loading,
      personalizeStep,
      setPersonalizeStep,
      setLoading,
      fetchOnboarding,
      step,
      setStep,
      fetching,
      gotLogoFromWebsite,
      setGotLogoFromWebsite
    }),
    [
      values,
      isStone,
      loading,
      personalizeStep,
      setPersonalizeStep,
      setLoading,
      status,
      handleChange,
      fetchOnboarding,
      step,
      setStep,
      fetching,
      gotLogoFromWebsite,
      setGotLogoFromWebsite
    ]
  )

  return (
    <OnboardingFormContext.Provider value={value}>
      {children}
    </OnboardingFormContext.Provider>
  )
}

export const useOnboardingContext = (): OnboardingContextProps => {
  const context = useContext(OnboardingFormContext)

  if (!context) {
    throw new Error(
      'useOnboardingContext should be used within OnboardingFormProvider.'
    )
  }

  return context
}
